import { api, tagTypes } from "./api";

type PaginationParams = {
  page?: number;
  limit?: number;
  search?: string;
  level?: string;
  artisanId?: string;
};

// Inject booking endpoints into the main API
export const bookingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Dashboard stats for bookings
    dashboardData: builder.query<any, void>({
      query: () => ({ url: "booking/v1/admin/dashboard", method: "GET" }),
      providesTags: [tagTypes.Booking],
    }),

    bookingHistory: builder.query<
      any,
      {
        booking_type?: string;
        page?: number;
        limit?: number;
        status?: string;
        search?: string;
        startDate?: Date;
        endDate?: Date;
      }
    >({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();

        // Dynamically add only the params that exist
        if (params.status !== undefined)
          queryParams.append("status", params.status);
        if (params.search !== undefined)
          queryParams.append("search", params.search);
        if (params.startDate) {
          queryParams.append(
            "start_date",
            params.startDate.toISOString().split("T")[0],
          );
        }

        if (params.endDate) {
          queryParams.append(
            "end_date",
            params.endDate.toISOString().split("T")[0],
          );
        }
        if (params.booking_type !== undefined)
          queryParams.append("booking_type", params.booking_type);
        if (params.page !== undefined)
          queryParams.append("page", params.page.toString());
        if (params.limit !== undefined)
          queryParams.append("limit", params.limit.toString());

        return {
          url: `booking/v1/admin/history?${queryParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: [tagTypes.Booking],
    }),

    // Artisan booking history
    artisanBookingHistory: builder.query<
      any,
      { artisanId: string; page?: number; limit?: number, status?: string }
    >({
      query: ({ artisanId, page = 1, limit = 10, status }) => ({
        url: `booking/v1/admin/artisan/${artisanId}/history`,
        method: "GET",
        params: { page, limit, status },
      }),
      providesTags: [tagTypes.Booking],
    }),

    partnersAnalytics: builder.query<
      any,
      { artisanId: string; level?: string }
    >({
      query: ({ artisanId, level }) => ({
        url: `booking/v1/partner-category/analytics?${level === "partner" ? "artisan" : level}Id=${artisanId}&level=${level}`,
        method: "GET",
        // params: { artisanId, level },
      }),
      providesTags: [tagTypes.Booking],
    }),

    generalPartnersAnalytics: builder.query<any, void>({
      query: () => ({
        url: `booking/v1/partner-category/analytics`,
        method: "GET",
        // params: { artisanId, level },
      }),
      providesTags: [tagTypes.Booking],
    }),

    // User booking history
    userBookingHistory: builder.query<
      any,
      { userId: string; page?: number; limit?: number, status?: string }
    >({
      query: ({ userId, page = 1, limit = 10, status }) => ({
        url: `booking/v1/admin/user/${userId}/history`,
        method: "GET",
        params: { page, limit, status },
      }),
      providesTags: [tagTypes.Booking],
    }),

    // Booking details
    bookingDetails: builder.query<any, string>({
      query: (bookingId) => ({
        url: `booking/v1/admin/history/${bookingId}`,
        method: "GET",
      }),
      providesTags: [tagTypes.Booking],
    }),

    // Booking messages
    getMessages: builder.query<any, string>({
      query: (bookingId) => ({
        url: `booking/v1/admin/messages/${bookingId}`,
        method: "GET",
      }),
      providesTags: [tagTypes.Booking],
    }),

    // Cancel booking
    cancelBooking: builder.mutation<any, string>({
      query: (bookingId) => ({
        url: `booking/v1/admin/${bookingId}/cancel`,
        method: "POST",
        body: { cancelled_by: "admin" },
      }),
      invalidatesTags: [tagTypes.Booking],
    }),

    // Accept booking
    acceptBooking: builder.mutation<any, string>({
      query: (bookingId) => ({
        url: `booking/v1/admin/${bookingId}/accept`,
        method: "POST",
      }),
      invalidatesTags: [tagTypes.Booking],
    }),


    // Accept booking
    completeBooking: builder.mutation<any, string>({
      query: (bookingId) => ({
        url: `booking/v1/admin/${bookingId}/complete`,
        method: "POST",
      }),
      invalidatesTags: [tagTypes.Booking],
    }),

    credential: builder.mutation<any, { id: string; item: string }>({
      query: ({ id, item }) => ({
        url: `artisan/v1/partner/${id}/${item}-credentials`,
        method: "POST",
      }),
      invalidatesTags: [tagTypes.Booking],
    }),

    // Export bookings
    exportBookings: builder.query<any, { booking_type?: string }>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        queryParams.append("export", "true");
        if (params.booking_type) {
          queryParams.append("booking_type", params.booking_type);
        }
        return {
          url: `booking/v1/admin/history?${queryParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: [tagTypes.Booking],
    }),

    // Currency details
    getCurrencyDetails: builder.query<
      any,
      { destinationCurrency: string; amount: number }
    >({
      query: ({ destinationCurrency, amount }) => ({
        url: `booking/v1/admin/currency_detail?includeExchange=true&destinationCurrency=${destinationCurrency}&amount=${amount}`,
        method: "GET",
      }),
      providesTags: [tagTypes.Booking],
    }),
  }),
});

export const {
  useDashboardDataQuery,
  useBookingHistoryQuery,
  useArtisanBookingHistoryQuery,
  usePartnersAnalyticsQuery,
  useGeneralPartnersAnalyticsQuery,
  useUserBookingHistoryQuery,
  useBookingDetailsQuery,
  useGetMessagesQuery,
  useCancelBookingMutation,
  useCompleteBookingMutation,
  useAcceptBookingMutation,
  useCredentialMutation,
  useExportBookingsQuery,
  useLazyExportBookingsQuery,
  useGetCurrencyDetailsQuery,
} = bookingApi;
